import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Crown, Globe2, Sparkles, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "@/components/site/Reveal";
import LeadForm from "@/components/site/LeadForm";
import Seo, { ORG_SCHEMA, WEBSITE_SCHEMA, breadcrumbSchema, faqSchema } from "@/components/site/Seo";
import { BRAND, SERVICES } from "@/lib/constants";

const TRUST = [
  { label: "British-Owned", text: "UK directors. UAE-licensed." },
  { label: "UAE Specialists", text: "On-the-ground government liaison." },
  { label: "Fast Processing", text: "Priority pathways for visas & licences." },
  { label: "Dedicated Manager", text: "One advisor. End-to-end." },
];

const TESTIMONIALS = [
  { name: "Charlotte H.", role: "Family relocating from London", quote: "Honestly thought moving the whole family to Dubai would be a nightmare. Velor sorted the visas, schools, even our bank accounts before we landed. We turned up and just… moved in. Couldn't recommend them more." },
  { name: "James R.", role: "Founder, FinTech", quote: "I needed a DIFC entity set up and my Golden Visa sorted at the same time — the team just got on with it. No chasing, no surprises, no nonsense. Refreshing after the firms I'd spoken to before." },
  { name: "Sophie & Mark", role: "Investors", quote: "We met a few other consultants in Dubai and they all felt very salesy. The team at Velor actually listened to us first, then told us what made sense. We've already sent two friends their way." },
];

const FAQS = [
  { q: "Who qualifies for a UAE Golden Visa?", a: "Investors, entrepreneurs, exceptional talent, scientists, top students and certain property owners. We assess your eligibility privately within 24 hours." },
  { q: "How long does business setup typically take?", a: "Free Zone formations often complete within 3–7 working days. Mainland LLCs and regulated structures (DIFC/ADGM) take longer. We'll provide a precise timeline after a brief consultation." },
  { q: "Do you handle the full relocation, not just visas?", a: "Yes — we offer concierge relocation: housing search, schools, banking, healthcare, vehicle and lifestyle setup. One point of contact throughout." },
  { q: "Can my family come with me?", a: "Absolutely. We sponsor spouses, children, parents and domestic staff under your residency or Golden Visa." },
  { q: "Why choose a British-owned firm in Dubai?", a: "Familiar standards of communication and transparency, combined with deep UAE government relationships built over years on the ground." },
  { q: "What does it cost?", a: "Fees depend on your structure and pathway. We provide written, all-inclusive quotes — no surprise charges, ever." },
];

export default function Home() {
  return (
    <div className="bg-white text-black">
      <Seo
        title="UAE Golden Visa, Business Setup, Relocation"
        description="British-owned UAE consultancy for Golden Visas, residency, company formation and relocation. Personal guidance for investors, founders and families."
        keywords="Velor Advisory, UAE Golden Visa, Dubai business setup, UAE residency, British consultancy Dubai, UAE relocation, company formation UAE, property investment Dubai"
        focusKeyword="British consultancy Dubai"
        path="/"
        schema={[
          ORG_SCHEMA,
          WEBSITE_SCHEMA,
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "@id": "https://velor.ae/#business",
            name: "Velor Advisory",
            image: "https://velor.ae/favicon.png",
            url: "https://velor.ae",
            telephone: "+971-000-000-000",
            priceRange: "$$$",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Dubai",
              addressCountry: "AE",
            },
            areaServed: [
              { "@type": "Country", name: "United Arab Emirates" },
              { "@type": "Country", name: "United Kingdom" },
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Advisory Services",
              itemListElement: [
                { "@type": "Offer", itemOffered: { "@type": "Service", name: "UAE Golden Visa" } },
                { "@type": "Offer", itemOffered: { "@type": "Service", name: "UAE Business Setup" } },
                { "@type": "Offer", itemOffered: { "@type": "Service", name: "UAE Relocation Planning" } },
              ],
            },
          },
          breadcrumbSchema([{ name: "Home", path: "/" }]),
          faqSchema(FAQS),
        ]}
      />
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/19664340/pexels-photo-19664340.jpeg?auto=compress&cs=tinysrgb&w=1650&fm=webp"
            alt="Dubai skyline over the creek at golden hour with the Burj Khalifa in the distance"
            width="1650"
            height="928"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/70" />
        </div>
        <div className="relative h-full mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <div className="text-white/85 text-[11px] tracking-overline uppercase animate-fade-up">British-Owned · UAE-Based</div>
          <h1 className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-light leading-[0.95] tracking-tight max-w-5xl animate-fade-up delay-1">
            Helping You Live, Invest and Build Your Future in the UAE.
          </h1>
          <p className="mt-7 text-white/80 max-w-xl text-base md:text-lg font-light leading-relaxed animate-fade-up delay-2">
            Golden Visas, residency, company formation and relocation planning—guided by a senior advisory team that provides personal support at every stage.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up delay-3">
            <a
              href={BRAND.calendly}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-book-consultation-btn"
              className="inline-flex items-center gap-3 px-8 py-5 bg-white text-black text-[12px] tracking-overline uppercase hover:bg-white/90 transition-colors"
            >
              Book Free Consultation <ArrowRight size={16} />
            </a>
            <a
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-speak-to-advisor-btn"
              className="inline-flex items-center gap-3 px-8 py-5 border border-white/60 text-white text-[12px] tracking-overline uppercase hover:bg-white hover:text-black transition-colors"
            >
              Speak to an Advisor
            </a>
          </div>
        </div>
      </section>

      {/* TRUST PILLARS */}
      <section className="border-t border-b border-black/10" data-testid="trust-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4">
          {TRUST.map((t, i) => (
            <Reveal key={t.label} delay={i * 80} className={`py-10 md:py-14 px-6 md:px-10 ${i !== 0 ? "md:border-l border-black/10" : ""} ${i % 2 !== 0 ? "border-l border-black/10 md:border-l" : ""} ${i >= 2 ? "border-t md:border-t-0 border-black/10" : ""}`}>
              <div className="text-[11px] tracking-overline uppercase text-black/45">0{i+1}</div>
              <div className="font-serif text-2xl md:text-3xl mt-3">{t.label}</div>
              <div className="text-sm text-black/55 mt-2 font-light leading-relaxed">{t.text}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-36" data-testid="intro-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">A Quieter Way to Dubai</div>
            <h2 className="font-serif text-4xl md:text-6xl mt-5 leading-[1.02]">
              The advisory<br/>of choice for those<br/><em className="italic">who value discretion.</em>
            </h2>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6 md:col-start-7">
            <p className="text-black/65 text-lg leading-relaxed font-light">
              Velor Advisory is a private consultancy founded by British professionals living
              in Dubai. We guide families, founders and high-net-worth individuals through every
              dimension of moving to the UAE — from Golden Visas and company formation to schools,
              banking and the small details that make a new life feel instantly familiar.
            </p>
            <div className="mt-10 max-w-md hidden">
              <div>
                <div className="font-serif text-5xl">98<span className="text-black/30">%</span></div>
                <div className="text-xs tracking-overline uppercase text-black/45 mt-1">Approval rate</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-t border-black/10" data-testid="services-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Our Services</div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-4">
              <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-2xl">A complete suite, delivered by one team.</h2>
              <Link to="/contact" data-testid="services-contact-link" className="text-[12px] tracking-overline uppercase border-b border-black pb-1 hover:opacity-60 transition self-start">Begin an enquiry →</Link>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-black/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 4) * 60} className="border-r border-b border-black/10 p-8 md:p-10 group hover:bg-[#fafafa] transition-colors">
                <div className="text-[11px] tracking-overline uppercase text-black/40">0{i+1}</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-4">{s.title}</h3>
                <p className="text-sm text-black/60 mt-3 font-light leading-relaxed">{s.short}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-overline uppercase">
                  <span className="border-b border-black pb-0.5">Learn more</span>
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GOLDEN VISA FEATURE */}
      <section className="bg-[#0a0a0a] text-white" data-testid="golden-visa-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img
                src="https://images.pexels.com/photos/21282689/pexels-photo-21282689.jpeg?auto=compress&cs=tinysrgb&w=1200&fm=webp"
                alt="Modern Dubai skyscraper facade — architectural detail representing the UAE Golden Visa"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[2000ms]"
              />
            </div>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6">
            <div className="flex items-center gap-3 text-[11px] tracking-overline uppercase text-white/55"><Crown size={14}/> The UAE Golden Visa</div>
            <h2 className="font-serif text-5xl md:text-6xl mt-5 leading-[1.02]">A decade of residency, on your terms.</h2>
            <p className="mt-7 text-white/70 leading-relaxed font-light max-w-lg">
              The UAE's 10-year Golden Visa offers self-sponsorship for investors, founders, exceptional talent
              and property owners. We assess eligibility privately, prepare your application, and liaise directly
              with the relevant UAE government authorities on your behalf.
            </p>
            <ul className="mt-10 space-y-4 text-sm font-light text-white/80 max-w-md">
              {["Sponsor spouse, children, parents & domestic workers", "Multiple-entry, long validity", "Property, investment, talent & founder pathways"].map((x) => (
                <li key={x} className="flex items-start gap-3"><Check size={16} className="mt-0.5 text-white/70"/>{x}</li>
              ))}
            </ul>
            <div className="mt-10 flex gap-4">
              <Link to="/golden-visa" data-testid="golden-visa-cta" className="inline-flex items-center gap-2 px-7 py-4 bg-white text-black text-[12px] tracking-overline uppercase hover:bg-white/85 transition">Explore Golden Visa <ArrowRight size={14}/></Link>
              <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="golden-visa-book" className="inline-flex items-center gap-2 px-7 py-4 border border-white/30 text-[12px] tracking-overline uppercase hover:bg-white hover:text-black transition">Check Eligibility</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BUSINESS SETUP */}
      <section className="py-24 md:py-36" data-testid="business-setup-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6 order-2 md:order-1">
            <div className="text-[11px] tracking-overline uppercase text-black/45 flex items-center gap-2"><Globe2 size={14}/> Business Setup</div>
            <h2 className="font-serif text-5xl md:text-6xl mt-5 leading-[1.02]">Establish your UAE business<br/>with confidence.</h2>
            <p className="mt-7 text-black/65 leading-relaxed font-light max-w-lg">
              From DIFC and ADGM to DMCC, IFZA and Mainland LLCs — we'll recommend the right
              jurisdiction, structure the shareholding and walk every document through to issuance.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              {["Mainland LLC", "Free Zone Co.", "DIFC / ADGM", "Holding Structures"].map((x) => (
                <div key={x} className="border-t border-black/15 pt-4">
                  <div className="text-sm font-medium">{x}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex gap-4">
              <Link to="/business-setup" data-testid="business-setup-cta" className="inline-flex items-center gap-2 px-7 py-4 bg-black text-white text-[12px] tracking-overline uppercase hover:bg-black/85 transition">Explore Business Setup <ArrowRight size={14}/></Link>
            </div>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6 order-1 md:order-2">
            <div className="aspect-[4/3] w-full overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1524230572899-a752b3835840?auto=format&fit=crop&w=1200&q=75"
                alt="Clean white modern office architecture — representing UAE business setup and company formation"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[2000ms]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELOCATION CONCIERGE */}
      <section className="bg-[#fafafa] border-y border-black/10" data-testid="relocation-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <div className="aspect-[4/3] w-full overflow-hidden">
              <img
                src="https://images.pexels.com/photos/19612315/pexels-photo-19612315.jpeg?auto=compress&cs=tinysrgb&w=1200&fm=webp"
                alt="Waterfront lifestyle view of Dubai Marina at dusk — representing UAE relocation and living"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[2000ms]"
              />
            </div>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6">
            <div className="text-[11px] tracking-overline uppercase text-black/45 flex items-center gap-2"><Sparkles size={14}/> Relocation Planning</div>
            <h2 className="font-serif text-5xl md:text-6xl mt-5 leading-[1.02]">Every aspect of your move,<br/>expertly managed.</h2>
            <p className="mt-7 text-black/65 leading-relaxed font-light max-w-lg">
              Housing search, school placement, healthcare, banking introductions, vehicle, drivers
              and even the right gym. One dedicated advisor takes the entire move off your hands.
            </p>
            <ul className="mt-10 grid grid-cols-2 gap-x-8 gap-y-3 text-sm font-light text-black/75 max-w-md">
              {["Housing search", "School placement", "Banking intros", "Healthcare", "Vehicle setup", "Property investments", "Lifestyle curation"].map((x) => (
                <li key={x} className="flex items-start gap-2"><Check size={15} className="mt-0.5"/>{x}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-36" data-testid="why-choose-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-overline uppercase text-black/45">Why Velor</div>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-[1.05] max-w-3xl">
              A consultancy built on <em className="italic">trust</em>, transparency, and time saved.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { t: "British Standards", d: "Communication, paperwork and confidentiality you'd expect from a London firm — delivered in Dubai." , i: ShieldCheck},
              { t: "Expert Guidance", d: "Work with experienced professionals who provide tailored advice and support throughout your journey.", i: Crown},
              { t: "All-Inclusive Pricing", d: "Written, fixed-fee proposals. No mystery charges, no per-page surprises.", i: Sparkles},
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 100}>
                <x.i size={22} className="text-black/65"/>
                <h3 className="font-serif text-2xl md:text-3xl mt-6">{x.t}</h3>
                <p className="text-black/60 mt-3 font-light leading-relaxed">{x.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-black/10 bg-[#0a0a0a] text-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-32">
          <div className="text-[11px] tracking-overline uppercase text-white/45">Clients</div>
          <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-tight max-w-3xl">Spoken about quietly, recommended often.</h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 100} className="bg-[#0a0a0a] p-10 md:p-12">
                <Quote className="text-white/30" size={22}/>
                <p className="mt-6 text-white/85 leading-relaxed font-light text-lg">"{t.quote}"</p>
                <div className="mt-10 text-[11px] tracking-overline uppercase">
                  <div className="text-white">{t.name}</div>
                  <div className="text-white/45 mt-1 normal-case tracking-normal text-xs font-light">{t.role}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32" data-testid="faq-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <div className="text-[11px] tracking-overline uppercase text-black/45">FAQ</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Common questions, clearly answered.</h2>
            <p className="text-black/60 mt-6 font-light max-w-sm">Can't find your answer? <Link to="/contact" data-testid="faq-contact-link" className="underline underline-offset-4">Ask us directly</Link>.</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-black/10 first:border-t-0">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-7 text-left font-serif text-xl md:text-2xl hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-black/65 font-light leading-relaxed text-base pb-7 max-w-2xl">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CONTACT / BOOK */}
      <section className="bg-[#fafafa] border-t border-black/10" data-testid="contact-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Begin Quietly</div>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-[1.02]">A short conversation. <em className="italic">No commitment.</em></h2>
            <p className="mt-6 text-black/65 font-light leading-relaxed max-w-md">
              Book a complimentary 30-minute consultation, or share your details and we'll come back to you privately.
            </p>
            <div className="mt-10 space-y-5 text-sm">
              <div>
                <div className="text-[11px] tracking-overline uppercase text-black/45">WhatsApp</div>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="block mt-1 text-lg">{BRAND.whatsapp}</a>
              </div>
              <div>
                <div className="text-[11px] tracking-overline uppercase text-black/45">Email</div>
                <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="block mt-1 text-lg">{BRAND.email}</a>
              </div>
              <a
                href={BRAND.calendly}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-calendly-btn"
                className="inline-flex items-center gap-3 mt-8 px-7 py-4 bg-black text-white text-[12px] tracking-overline uppercase hover:bg-black/85 transition"
              >
                Schedule via Calendly <ArrowRight size={14}/>
              </a>
            </div>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <LeadForm testid="home-lead-form" source="homepage" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
